import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, GetProp, Image, Select, Upload } from 'antd';
import { UploadFile, UploadProps } from 'antd/es/upload';
import { getApi, postApi, putApi } from 'apis';
import AutoCompleteField from 'components/Global/AutoCompleteField';
import InputWithLabelAndSvg from 'components/Global/InputWithLabel';
import { FORMDATA, sanitizeString } from 'constants/Data';
import { getNames } from 'country-list';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { toastText, checkPermission } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
import AddInput from 'components/Global/AddInput';
import WarningTagsInput from 'components/Global/WarningTagsInput';
import {
	buildMedicineClipboardPayload,
	loadMedicineClipboardPayload,
	normalizeClipboardFieldsForPaste,
	saveMedicineClipboardPayload,
} from '../medicineFormClipboard';

function cloneClipboardFields(fields: Record<string, unknown>): Record<string, unknown> {
	try {
		if (typeof structuredClone === 'function') {
			return structuredClone(fields);
		}
	} catch {
		// structuredClone can throw on non-cloneable values; fall back below
	}
	return JSON.parse(JSON.stringify(fields)) as Record<string, unknown>;
}

function normalizeMedicineNameForSubmit(value: string): string {
	return sanitizeString(value).trim().replace(/\s+/g, ' ');
}

function isEditableEventTarget(target: EventTarget | null): boolean {
	const element = target as HTMLElement | null;
	if (!element) return false;
	const editableSelector =
		'input, textarea, [contenteditable=""], [contenteditable="true"], [role="textbox"], [role="combobox"], .ant-select, .ant-select-selector, .ant-select-selection-search-input';
	return Boolean(element.closest(editableSelector));
}

type Category = {
	id: string;
	name: string;
	subCategories: {
		id: string;
		name: string;
		categoryId: string;
	}[];
};

const AddMedicinesBody: FC<any> = (props) => {
	// Inits
	const { addMedicineFields } = FORMDATA;
	const {
		closeDrawerByAnimation,
		editSelectedMedicine,
		fetchMedicines,
		setEditSelectedMedicine,
		setOptionsState,
		optionsState
	} = props;

	const medicinesPermissions = useSelector(
		(state: any) => state?.userProfile
	);

	const isImageEditPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Images',
		permission: ['edit'],
	});

	const isImageAddPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Images',
		permission: ['add'],
	});

	const isImageViewPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Images',
		permission: ['view'],
	});

	const isImageDeletePermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Images',
		permission: ['delete'],
	});

	// Image fields shown only if user has view permission
	const canViewImages = isImageViewPermission;

	// Can delete images if user has edit or delete permission
	const canDeleteImages = isImageEditPermission || isImageDeletePermission;

	const isMedicineEditPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Medicines',
		permission: ['edit'],
	});

	const isMedicineAddPermission = checkPermission(medicinesPermissions?.data?.permissions, {
		permissionName: 'Medicines',
		permission: ['add'],
	});

	// For edit mode: check edit permission, for add mode: check add permission
	const canManageMedicines = editSelectedMedicine 
		? isMedicineEditPermission 
		: isMedicineAddPermission;

	// If user has image view permission but not medicine permission, disable all non-image fields
	const shouldDisableNonImageFields = canViewImages && !canManageMedicines;

	type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
	const [form] = Form.useForm();
	const [loading, setLoading] = useState<boolean>();
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState('');
	const [categoryList, setCategoryList] = useState([{ label: '', value: '' }]);
	const [subCategoryList, setSubCategoryList] = useState([{ label: '', value: '' }]);
	const [doseForms, setDoseForms] = useState<{ label: string; value: string }[]>([]);
	const [doseFormOptionsForCategory, setDoseFormOptionsForCategory] = useState<{ label: string; value: string }[]>([]);
	const [loadingDoseFormsByCategory, setLoadingDoseFormsByCategory] = useState(false);
	const [warningTags, setWarningTags] = useState<{ label: string; value: string }[]>([]);
	const [selectedCategoryId, setSelectedCategoryId] = useState('');
	const [wholeCategories, setWholeCategories] = useState([{}]);
	const subCategoryOptions = useMemo(() => {
		return (
			(wholeCategories as Category[])?.find(
				(item: any) => item?.id === selectedCategoryId
			)?.subCategories?.map((sub: any) => ({
				label: sub.subCategoryName,
				value: sub.id,
			})) || []
		);
	}, [wholeCategories, selectedCategoryId]);


	const countries = getNames().map((name) => {
		if (name === 'Korea, Republic of') {
			return { label: 'South Korea', value: 'South Korea' };
		}
		if (name === "Korea, Democratic People's Republic of") {
			return { label: 'North Korea', value: 'North Korea' };
		}
		return { label: name, value: name };
	});

	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const isApplyingClipboardRef = useRef(false);
	const debounceDelay = 400;

	const STRENGTH_AUTO_UNITS = ['TABLET', 'CAPSULE'];
	const isStrengthAutoUnit = (unit: unknown) =>
		typeof unit === 'string' && STRENGTH_AUTO_UNITS.includes(unit);
	const deriveStrengthFromSalt = (salt: unknown): string =>
		(Array.isArray(salt) ? salt : [])
			.map((entry) => String((entry as { strength?: string })?.strength ?? '').trim())
			.filter(Boolean)
			.join('/');
	const syncStrengthFromSalt = (saltVal: unknown) => {
		if (!isStrengthAutoUnit(form.getFieldValue('unitOfMeasure'))) return;
		form.setFieldsValue({ medicineStrength: deriveStrengthFromSalt(saltVal) });
	};

	const toOptions = (values: unknown[]): { label: string; value: string }[] => {
		const unique = new Set<string>();
		for (const v of values) {
			if (typeof v === 'string' && v) unique.add(v);
		}
		return Array.from(unique).map((v) => ({ label: v, value: v }));
	};
	
	const editData = {
		...editSelectedMedicine,
		medicineSecondaryImageUrl: editSelectedMedicine?.medicineSecondaryImageUrl || [],
		medicinePrimaryImageUrl: editSelectedMedicine?.medicinePrimaryImageUrl || '',
	};
	const initialImg = editData?.medicineSecondaryImageUrl.map((item: any, index: number) => {
		return {
			uid: index.toString(),
			name: item,
			status: 'done',
			url: `${process.env.REACT_APP_S3_BASE_URL}${item}`
		}
	})
	const [fileList, setFileList] = useState<UploadFile[]>(initialImg);
	const [primaryFileList, setPrimaryFileList] = useState<UploadFile[]>(
		editData?.medicinePrimaryImageUrl
			? [{
				uid: '1',
				name: editData.medicinePrimaryImageUrl,
				url: `${process.env.REACT_APP_S3_BASE_URL}${editData.medicinePrimaryImageUrl}`
			}]
			: []
	);



	const handleSearch = useCallback(async (value: string, field: keyof typeof optionsState) => {

		const stripTags = (str: string) => str?.replace(/<\/?[^>]+(>|$)/g, '');

		let searchValue = stripTags(value?.trim() || "");
		if (field === "usesTags") {
			const parts = value.split(",");
			searchValue = stripTags(parts[parts.length - 1]?.trim() || "");
		}
		const sanitizedValue = stripTags(value?.trim() || '');

		// Only continue if value has at least 3 characters
		if (!searchValue || !sanitizedValue || sanitizedValue.length < 3 || field === 'shortDescription') {
			setOptionsState((prev: any) => ({ ...prev, [field]: [] }));
			return;
		}


		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		debounceTimeoutRef.current = setTimeout(async () => {
			if (!sanitizedValue) {
				setOptionsState((prev: any) => ({ ...prev, [field]: [] }));
				return;
			}

			try {
				const query = {
					targetField: field,
					search: searchValue
					// Not passing isQualityCheck to search all medicines
				};

				const res = await getApi("/medicine/get-all", query);
				let formattedOptions: { label: string; value: string }[] = [];

				if (field === 'saltComposition') {
					const salts = res?.data?.data?.flatMap((item: any) => item?.saltComposition || []);
					const names = salts.map((s: any) => s?.name);
					const filtered = (names as unknown[]).filter((n) =>
						typeof n === 'string' && n.toLowerCase().includes(sanitizedValue.toLowerCase())
					);
					formattedOptions = toOptions(filtered);
				} else if (field === 'usesTags') {
					const tags = res?.data?.data?.flatMap((item: any) => Array.isArray(item?.usesTags) ? item.usesTags : []);
					formattedOptions = toOptions(tags as unknown[]);
				} else {
					const fieldValues = res?.data?.data.map((item: any) => item[field]);
					formattedOptions = toOptions(fieldValues as unknown[]);
				}

				setOptionsState((prev: any) => ({ ...prev, [field]: formattedOptions }));
			} catch (e) {
				setOptionsState((prev: any) => ({ ...prev, [field]: [] }));
			}
		}, debounceDelay);
	}, [setOptionsState]);

	const onFinish = async (values: any) => {
		if (loading) return;
		const { primaryImage, secondaryImage, ...restData } = values;
		setLoading(true);
		const formData: any = new FormData();

		// Only process images if user has Images edit or add permission
		const canManageImagesForSubmit = isImageEditPermission || isImageAddPermission;
		if (canManageImagesForSubmit) {
			// Primary image - single file logic
			let primaryImageUrl: File | null = null;
			let existingPrimaryImageUrl: string | null = null;

			if (primaryFileList && primaryFileList.length) {
				const file = primaryFileList[0];

				if ('originFileObj' in file && file.originFileObj instanceof File) {
					primaryImageUrl = file.originFileObj; // New file
				} else if (file.url) {
					existingPrimaryImageUrl = file.name; // Keep existing
				}
			}

			// Handle primary image
			if (primaryImageUrl) {
				formData.append('medicinePrimaryImageUrl', primaryImageUrl);
			} else if (existingPrimaryImageUrl) {
				formData.append('existingMedicinePrimaryImageUrl', existingPrimaryImageUrl);
			}
			// If neither exists, image is deleted (implicit)

			const existingSecondaryImageUrls: string[] = [];
			const newFiles: File[] = [];


			// for secondary Images 
			if (fileList) {
				// Separate new files and existing URLs
				fileList.forEach((file) => {
					if ('originFileObj' in file && file.originFileObj instanceof File) {
						newFiles.push(file.originFileObj);
					} else if (file.url) {
						existingSecondaryImageUrls.push(file?.name);
					}
				});
			}


			// Append new files to FormData for S3 upload
			newFiles.forEach((file) => {
				formData.append('medicineSecondaryImageUrl', file);
			});

			if (editSelectedMedicine && existingSecondaryImageUrls) {
				formData.append('existingMedicineSecondaryImageUrls', JSON.stringify(existingSecondaryImageUrls));
			}
		} else if (editSelectedMedicine) {
			// If editing without image permission, preserve existing images
			if (editData?.medicinePrimaryImageUrl) {
				formData.append('existingMedicinePrimaryImageUrl', editData.medicinePrimaryImageUrl);
			}
			if (editData?.medicineSecondaryImageUrl && editData.medicineSecondaryImageUrl.length > 0) {
				formData.append('existingMedicineSecondaryImageUrls', JSON.stringify(editData.medicineSecondaryImageUrl));
			}
		}

		if (typeof restData.usesTags === 'string') {
			restData.usesTags = restData.usesTags.split(',').map((tag: any) => tag.trim());
		}

		if (restData.itemSize === undefined || restData.itemSize === null) {
			restData.itemSize = '';
		}

		if (!restData.numberOfItems || String(restData.numberOfItems).trim() === '') {
			restData.numberOfItems = '1';
		}

		// Filter out empty salt composition entries (where name or strength is empty)
		if (Array.isArray(restData.saltComposition)) {
			restData.saltComposition = restData.saltComposition.filter((item: any) => {
				if (typeof item === "object" && item !== null) {
					const name = item.name?.trim() || "";
					const strength = item.strength?.trim() || "";
					return name !== "" && strength !== "";
				}
				return false;
			});
		}

		Object.entries(restData).forEach(([key, value]) => {
			// Do not send null-like values; avoids persisting the literal string "null".
			if (value === undefined || value === null) return;

			if (Array.isArray(value)) {
				// Handle arrays (e.g., saltComposition)
				value.forEach((item, index) => {
					if (item === undefined || item === null) return;
					if (typeof item === "object") {
						// Convert object to JSON string before appending
						formData.append(`${key}[${index}]`, JSON.stringify(item));
					} else {
						formData.append(`${key}[${index}]`, item);
					}
				});
			} else if (
				typeof value === 'object' &&
				value !== null &&
				'value' in value &&
				'label' in value
			) {
				// Handle Select-like object (e.g., AntD Select)
				formData.append(key, sanitizeString((value as { value: string }).value));
			} else if (typeof value === 'string') {
				// Handle simple strings
				if (key === 'medicineName') {
					formData.append(key, normalizeMedicineNameForSubmit(value));
				} else {
					formData.append(key, sanitizeString(value));
				}
			} else {
				// Handle numbers, booleans, etc.
				formData.append(key, String(value));
			}
		});

		try {
			setLoading(true);
			let res;
			if (editSelectedMedicine) {
				res = await putApi(`/medicine/update/${editData?.id}`, formData, true);
			} else {
				res = await postApi('/medicine/create', formData, true);
			}

			// Check response
			if (res?.data?.responseStatus === 200) {
				toast.success(res?.data?.message);
				fetchMedicines();
				closeDrawerByAnimation();
			} else {
				throw new Error(res?.data?.message || 'Something went wrong.');
			}
		} catch (error: any) {
			const isNetworkError =
				!error?.response &&
				(error?.code === 'ERR_NETWORK' || error?.message === 'Network Error');
			toast.error(
				error?.response?.data?.message ||
					error?.response?.data?.error?.errorDescription ||
					(isNetworkError
						? 'Upload failed or connection was lost. Please try again with smaller images.'
						: null) ||
					error.message ||
					'Something went wrong.',
			);
			// closeDrawerByAnimation();
		} finally {
			setLoading(false);
		}
	};

	const handleOnClick = () => {
		closeDrawerByAnimation();
		setEditSelectedMedicine(null);
	}

	const handlePreview = async (file: UploadFile) => {
		if (!canViewImages) {
			return; // Don't allow preview if user doesn't have view permission
		}
		
		if (!file.url && !file.preview) {
			file.preview = await getBase64(file.originFileObj as FileType);
		}

		setPreviewImage(file.url || (file.preview as string));

		setPreviewOpen(true);
	};

	const MAX_IMAGE_SIZE_BYTES = 1 * 1024 * 1024; // 1MB

	const isImageWithinSizeLimit = (file?: UploadFile | File | null) => {
		const size =
			(file as UploadFile)?.size ??
			(file as File)?.size ??
			(file as UploadFile)?.originFileObj?.size;
		if (size == null) return true; // existing URL-only items have no size
		return size < MAX_IMAGE_SIZE_BYTES;
	};

	const beforeUploadImage = (file: File) => {
		if (!isImageWithinSizeLimit(file)) {
			toast.error('Image must be smaller than 1MB!');
			return Upload.LIST_IGNORE;
		}
		return false; // Prevent automatic upload; keep in list for FormData submit
	};

	const handleChange = (
		{ fileList: newFileList }: { file: UploadFile; fileList: UploadFile[] },
		type: 'primary' | 'secondary'
	) => {
		const filteredList = newFileList.filter((item) => {
			if (!isImageWithinSizeLimit(item)) {
				return false;
			}
			return true;
		});

		if (filteredList.length !== newFileList.length) {
			toast.error('Image must be smaller than 1MB!');
		}

		if (type === 'primary') {
			setPrimaryFileList(filteredList);
		} else {
			setFileList(filteredList);
		}
	};

	const canUploadImages = isImageEditPermission || isImageAddPermission;
	
	const uploadButton = (
		<button 
			style={{ border: 0, background: 'none' }} 
			type="button"
			disabled={!canUploadImages}
		>
			<PlusOutlined />
			<div style={{ marginTop: 8 }}>Upload</div>
		</button>
	);

	const getBase64 = (file: FileType): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});

	const propsUpload = {
		name: 'file',
		accept: '.jpg,.jpeg,.png,.webp',
		maxCount: 3,
		multiple: true,
		fileList: fileList,
		beforeUpload: beforeUploadImage,
		listType: 'picture-card',
		onPreview: canViewImages ? handlePreview : undefined,
		onRemove: canDeleteImages ? undefined : () => false,
		showUploadList: {
			showPreviewIcon: canViewImages,
			showRemoveIcon: canDeleteImages,
		},
	};

	const propsUploadPrimary = {
		name: 'file',
		accept: '.jpg,.jpeg,.png,.webp',
		maxCount: 1,
		multiple: true,
		fileList: primaryFileList,
		beforeUpload: beforeUploadImage,
		onPreview: canViewImages ? handlePreview : undefined,
		onRemove: canDeleteImages ? undefined : () => false,
		showUploadList: {
			showPreviewIcon: canViewImages,
			showRemoveIcon: canDeleteImages,
		},
	};

	const fetchWarningTagData = async () => {
		try {
			const res = await getApi('/warning-tags/get-all');
			if (res?.data?.responseStatus === 200) {
				const warningTags = res?.data?.data || [];
				const formattedWarningTags = warningTags.map((tag: any) => ({
					label: tag.tagName,
					value: tag.id,
				}));
				setWarningTags(formattedWarningTags);
			} else {
				toastText(res?.data?.message, 'error');
			}
		} catch (error: any) {
			toastText(
				error?.response?.data?.error?.errorDescription ?? 'Something went wrong',
				'error'
			);
		}
	};

	const fetchCategories = async () => {
		try {
			const res = await getApi('/category/get-all');

			if (res?.data?.responseStatus !== 200) {
				throw new Error(res?.data?.message || 'Failed to fetch categories.');
			}

			const categories = res.data.data || [];
			setWholeCategories(categories);

			const categoryOptions = categories.map((item: any) => ({
				label: item?.categoryName,
				value: item?.id,
			}));

			const subCategoryOptions = categories
				.flatMap((item: any) => item?.subCategories || [])
				.map((subItem: any) => {
					return ({
						label: subItem?.subCategoryName,
						value: subItem?.id,
					});
				});

			setCategoryList(categoryOptions);
			setSubCategoryList(subCategoryOptions);
		} catch (error: any) {
			toastText(error?.response?.data?.message || error.message, 'error');
		}
	};

	const fetchDoseForms = async () => {
		try {
			const res = await getApi('/dose-form/get-all');
			if (res?.data?.responseStatus !== 200) {
				throw new Error(res?.data?.message || 'Failed to fetch dose forms.');
			}

			const doseForms = res.data.data || [];
			const doseFormOptions = doseForms.map((item: any) => ({
				label: item?.name,
				value: item?.id,
			}));
			setDoseForms(doseFormOptions);
		} catch (error: any) {
			toastText(error?.response?.data?.message || error.message, 'error');
		}
	};

	const fetchDoseFormsByCategory = useCallback(
		async (categoryId: string): Promise<{ label: string; value: string }[]> => {
			if (!categoryId) {
				setDoseFormOptionsForCategory([]);
				return [];
			}
			setLoadingDoseFormsByCategory(true);
			try {
				const res = await getApi(`/category-dose-form-mapping/get-by-category/${categoryId}`);
				const mapped = (res?.data?.data || []).map((d: { id: string; name: string }) => ({
					label: d.name,
					value: d.id,
				}));
				const currentDoseFormId = editData?.doseFormId;
				if (
					currentDoseFormId &&
					!mapped.some((o: { value: string }) => o.value === currentDoseFormId) &&
					doseForms.length
				) {
					const current = doseForms.find((df) => df.value === currentDoseFormId);
					if (current) mapped.push(current);
				}
				setDoseFormOptionsForCategory(mapped);
				return mapped;
			} catch (error: any) {
				setDoseFormOptionsForCategory(doseForms);
				return doseForms;
			} finally {
				setLoadingDoseFormsByCategory(false);
			}
		},
		[doseForms, editData?.doseFormId]
	);

	const handleCopyMedicineFields = useCallback(() => {
		if (!editSelectedMedicine) return;
		const values = form.getFieldsValue(true) as Record<string, unknown>;
		const payload = buildMedicineClipboardPayload(values);
		saveMedicineClipboardPayload(payload);
		toast.success('Medicine fields copied');
	}, [editSelectedMedicine, form]);

	const handlePasteMedicineFields = useCallback(async () => {
		if (editSelectedMedicine) return;
		const payload = loadMedicineClipboardPayload();
		if (!payload) {
			toast.error('No copied medicine data. Use Copy while editing a medicine first.');
			return;
		}
		const fields = normalizeClipboardFieldsForPaste(
			cloneClipboardFields(payload.fields as Record<string, unknown>)
		);
		const catId = typeof fields.categoryId === 'string' ? fields.categoryId : '';
		flushSync(() => {
			setSelectedCategoryId(catId);
		});
		if (catId) {
			await fetchDoseFormsByCategory(catId);
		} else {
			await fetchDoseFormsByCategory('');
		}
		isApplyingClipboardRef.current = true;
		form.setFieldsValue(fields);
		setTimeout(() => {
			isApplyingClipboardRef.current = false;
		}, 0);
		setPrimaryFileList([]);
		setFileList([]);
		setPreviewImage('');
		setPreviewOpen(false);
		toast.success('Pasted medicine fields');
	}, [editSelectedMedicine, fetchDoseFormsByCategory, form]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (isEditableEventTarget(event.target)) return;
			if (!(event.ctrlKey && event.altKey)) return;
			const key = event.key.toLowerCase();

			if (key === 'v') {
				if (editSelectedMedicine || loading || shouldDisableNonImageFields || !isMedicineAddPermission) return;
				event.preventDefault();
				void handlePasteMedicineFields();
				return;
			}

			if (key === 'c') {
				const canUseCopyShortcut =
					Boolean(editSelectedMedicine) &&
					(isMedicineEditPermission || isImageEditPermission) &&
					!loading;
				if (!canUseCopyShortcut) return;
				event.preventDefault();
				handleCopyMedicineFields();
			}

		};

		window.addEventListener('keydown', onKeyDown);
		return () => {
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [
		editSelectedMedicine,
		handleCopyMedicineFields,
		handlePasteMedicineFields,
		isImageEditPermission,
		isMedicineAddPermission,
		isMedicineEditPermission,
		loading,
		shouldDisableNonImageFields,
	]);

	const getDropdownOptions = (fieldId: string, roleOptions?: any[]) => {
		switch (fieldId) {
			case "countryOfOrigin":
				return countries;
			case "categoryId":
				return categoryList;
			case "subCategoryId":
				return subCategoryOptions;
			case "doseFormId":
				return selectedCategoryId ? doseFormOptionsForCategory : [];
			default:
				return roleOptions?.map((opt: any) => ({
					label: opt.label,
					value: opt.value,
				})) || [];
		}
	};

	useEffect(() => {
		fetchCategories();
		fetchDoseForms();
		fetchWarningTagData();
	}, [])


	useEffect(() => {
		if (editSelectedMedicine && categoryList.length > 0 && subCategoryList.length > 0) {
			setSelectedCategoryId(editSelectedMedicine.categoryId);
		}
	}, [categoryList, subCategoryList, editSelectedMedicine]);

	useEffect(() => {
		if (selectedCategoryId) {
			fetchDoseFormsByCategory(selectedCategoryId);
		} else {
			setDoseFormOptionsForCategory([]);
		}
	}, [selectedCategoryId, fetchDoseFormsByCategory, doseForms]);

	useEffect(() => {
		const existingData = {
			liver: editData?.Warnings?.liver,
			liverTagId: editData?.Warnings?.liverTagId,
			kidney: editData?.Warnings?.kidney,
			kidneyTagId: editData?.Warnings?.kidneyTagId,
			alcohol: editData?.Warnings?.alcohol,
			alcoholTagId: editData?.Warnings?.alcoholTagId,
			pregnancy: editData?.Warnings?.pregnancy,
			pregnancyTagId: editData?.Warnings?.pregnancyTagId,
			driving: editData?.Warnings?.driving,
			drivingTagId: editData?.Warnings?.drivingTagId,
			lactation: editData?.Warnings?.lactation,
			lactationTagId: editData?.Warnings?.lactationTagId,
		};

		form.setFieldsValue(existingData);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- form init from editData once
	}, []);

	// JSX
	return (
		<div className={styles['side-drawer-body']}>
			<Form
				form={form}
				name="basic"
				initialValues={{
					...editData,
					mrp: editData?.mrp?.toString(),
					weightage: editData?.weightage?.toString(),
					gstPercentage: editData?.gstPercentage?.toString(),
					numberOfItems: editData?.numberOfItems ?? '1',
					categoryId: editData?.categoryId,
					subCategoryId: editData?.subCategoryId,
					description: editData?.description,
					usesTags: editData?.usesTags?.join(', ') || '',
				}}
				onFinish={onFinish}
				autoComplete="off"
				layout="vertical"
				labelAlign="left"
				className={styles['side-drawer-form']}
			>
				<div className={styles['side-drawer-form__inputs']}>
					{addMedicineFields.map(
						(singleField: any, index: number) => {
							// Skip image fields if user doesn't have Images view permission
							if ((singleField.type === 'primaryImage' || singleField.type === 'secondaryImage') && !canViewImages) {
								return null;
							}

							return (
								<React.Fragment key={index}>
									{singleField.type === 'text' && (
										<div className={shouldDisableNonImageFields ? styles["side-drawer-form__disabled-field"] : ''}>
											<AutoCompleteField
												form={form}
												singleUserInput={singleField}
												options={optionsState[singleField?.name as keyof typeof optionsState]}
												onSearch={handleSearch}
												disabled={shouldDisableNonImageFields}
											/>
										</div>
									)}

									{singleField.type === 'number' && (
										<div className={shouldDisableNonImageFields ? styles["side-drawer-form__disabled-field"] : ''}>
											<InputWithLabelAndSvg
												singleUserInput={singleField}
												disabled={shouldDisableNonImageFields}
											/>
										</div>
									)}

									{singleField.type === 'dropdown' && (
										<Form.Item
											key={singleField.id}
											name={singleField.name}
											label={singleField.title}
											rules={singleField.rules}
											className={`${styles["side-drawer-form__medicine--select"]} ${shouldDisableNonImageFields ? styles["side-drawer-form__medicine--select-disabled"] : ''}`}
										>
											<Select
												placeholder={singleField.id === 'doseFormId' && !selectedCategoryId ? 'Select category first' : singleField.placeHolder}
												options={getDropdownOptions(singleField.id, singleField.roleOptions)}
												showSearch
												allowClear={!singleField.required}
												optionFilterProp="label"
												style={{ height: "42px" }}
												disabled={shouldDisableNonImageFields}
												loading={singleField.id === 'doseFormId' ? loadingDoseFormsByCategory : false}
												onChange={(val: any) => {
													if (singleField.id === "categoryId") {
														setSelectedCategoryId(val || '');
														if (!isApplyingClipboardRef.current) {
															form.setFieldsValue({ subCategoryId: undefined, doseFormId: undefined });
														}
													}
													if (singleField.id === 'unitOfMeasure' && !isApplyingClipboardRef.current) {
														if (isStrengthAutoUnit(val)) {
															form.setFieldsValue({
																medicineStrength: deriveStrengthFromSalt(form.getFieldValue('saltComposition')),
															});
														} else {
															form.setFieldsValue({ medicineStrength: '' });
														}
													}
												}}
											/>
										</Form.Item>
									)}

									{singleField.type === 'primaryImage' && (
										<Form.Item label="Primary Image" name={singleField.name}>
											<div>
												<div className='images-wrap'>
													<Upload
														{...propsUploadPrimary}
														listType="picture-card"
														fileList={primaryFileList}
														onChange={(info) => handleChange(info, 'primary')}
														itemRender={(originNode) => (
															<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
																{originNode}
															</div>
														)}

													>
														{primaryFileList.length >= 1 ? null : uploadButton}
													</Upload>
												</div>

												{/* Image preview */}
												{previewImage && (
													<Image
														wrapperStyle={{ display: 'none' }}
														preview={{
															visible: previewOpen,
															onVisibleChange: (visible) => setPreviewOpen(visible),
															afterOpenChange: (visible) => !visible && setPreviewImage(''),
														}}
														src={previewImage}
													/>
												)}
											</div>
										</Form.Item>
									)}

									{singleField.type === 'secondaryImage' && (
										<Form.Item label="Secondary Image" name={singleField.name}>
											<div>
												<div className='images-wrap'>
													<Upload
														{...propsUpload}
														listType="picture-card"
														fileList={fileList}
														onChange={(info) => handleChange(info, 'secondary')}
														itemRender={(originNode) => (
															<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
																{originNode}
															</div>
														)}

													>
														{fileList.length >= 3 ? null : uploadButton}
													</Upload>
												</div>

												{/* Image preview */}
												{previewImage && (
													<Image
														wrapperStyle={{ display: 'none' }}
														preview={{
															visible: previewOpen,
															onVisibleChange: (visible) => setPreviewOpen(visible),
															afterOpenChange: (visible) => !visible && setPreviewImage(''),
														}}
														src={previewImage}
													/>
												)}
											</div>
										</Form.Item>
									)}

									{singleField.type === 'addInput' && (
										<div className={shouldDisableNonImageFields ? styles["side-drawer-form__disabled-field"] : ''}>
											<Form.Item
												key={singleField.id}
												name={singleField.name}
												rules={singleField.rules}
											>
												<AddInput
													label={singleField.title}
													name1="name"
													name2="strength"
													placeholder1="Salt Name"
													placeholder2="Strength"
													required={singleField.required}
													value={form.getFieldValue(singleField?.name) || [{ name: '', strength: '' }]}
													onChange={(val) => {
														form.setFieldsValue({ [singleField.name]: val });
														if (singleField.name === 'saltComposition') {
															syncStrengthFromSalt(val);
														}
													}}
													options={singleField.name === 'saltComposition' ? optionsState.saltComposition : undefined}
													onSearch={singleField.name === 'saltComposition' ? (val) => handleSearch(val, 'saltComposition') : undefined}
													isError={false}
													disabled={shouldDisableNonImageFields}
												/>
											</Form.Item>
										</div>
									)}

									{singleField.type === 'warning' && (
										<div className={shouldDisableNonImageFields ? styles["side-drawer-form__disabled-field"] : ''}>
											<Form.Item noStyle>
												<WarningTagsInput
													label={singleField.title}
													name1={singleField.name1}
													name2={singleField.name2}
													placeholder1="Enter warning"
													placeholder2="Enter warning tag"
													rules={singleField.rules}
													warningTags={warningTags}
													disabled={shouldDisableNonImageFields}
												/>
											</Form.Item>
										</div>
									)}
								</React.Fragment>
							);
						}
					)}
				</div>

				<div className={styles['side-drawer-form__buttons']}>
					{!editSelectedMedicine && isMedicineAddPermission && (
						<Button
							type="default"
							className={styles['side-drawer-form__buttons--cancel']}
							onClick={handlePasteMedicineFields}
							disabled={loading || shouldDisableNonImageFields}
						>
							Paste
						</Button>
					)}
					<Button
						htmlType="submit"
						disabled={loading}
						className={styles['side-drawer-form__buttons--edit']}
						loading={loading}
					>
						{editSelectedMedicine ? 'Update' : 'Save'}
					</Button>

					{editSelectedMedicine && (isMedicineEditPermission || isImageEditPermission) && (
						<Button
							type="default"
							className={styles['side-drawer-form__buttons--cancel']}
							onClick={handleCopyMedicineFields}
							disabled={loading}
						>
							Copy
						</Button>
					)}

					<Button
						htmlType="reset"
						className={styles['side-drawer-form__buttons--cancel']}
						onClick={handleOnClick}
					>
						Cancel
					</Button>
				</div>
			</Form>
		</div>
	);
};

export default AddMedicinesBody;


